import axios from 'axios';
import { AUTH_ENDPOINTS } from './endpoints';
import { TOKEN_TYPE, getTokenFromCookies, clearAllTokens } from '../../../lib/cookies';

// Base URL for auth service - hardcoded to ensure it works in production
const AUTH_BASE_URL = 'https://auth-446608.as.r.appspot.com';

// Create dedicated axios instance for auth API with absolute URL
const authAxios = axios.create({
  baseURL: AUTH_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
authAxios.interceptors.request.use((config) => {
  const token = getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle authentication errors
authAxios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.log('Auth API error:', error.response?.status, error.response?.data);

    // Handle 401 errors
    if (error.response?.status === 401) {
      const hasToken = getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN);
      if (hasToken) {
        console.log('Clearing tokens due to 401');
        clearAllTokens();
        window.location.href = '/auth/login';
      }
    }

    // Return error response
    return error.response || Promise.reject(error);
  }
);

export const authAPI = {
  // Password login
  login: async (email: string, password: string) => {
    const response = await authAxios.post(AUTH_ENDPOINTS.passwordLogin, {
      email,
      password,
    });
    return response.data;
  },

  // Register
  register: async (email: string, password: string, displayName: string) => {
    const response = await authAxios.post(AUTH_ENDPOINTS.passwordRegister, {
      email,
      password,
      displayName,
    });
    return response.data;
  },

  // OTP login
  otpLogin: async (email: string, otpCode: string) => {
    const response = await authAxios.post(AUTH_ENDPOINTS.otpLogin, {
      email,
      otpCode,
    });
    return response.data;
  },

  // Request OTP
  requestOTP: async (email: string) => {
    const response = await authAxios.post(AUTH_ENDPOINTS.otpRequest, { email });
    return response.data;
  },

  // Refresh token
  refreshToken: async (refreshToken: string) => {
    const response = await authAxios.post('/api/public/auth/refresh', {
      refreshToken,
    });
    return response.data;
  },

  // Get current user
  me: async () => {
    const response = await authAxios.get(AUTH_ENDPOINTS.me);
    return response.data;
  },

  // Logout
  logout: async () => {
    const response = await authAxios.post(AUTH_ENDPOINTS.logout);
    return response.data;
  },
};